'use client';

import Image from 'next/image';
import { Phone, Mail, Printer, MapPin, Clock, Star } from 'lucide-react';
import { useQuery } from '@apollo/client/react';
import {
  SALES_REPRESENTATIVE_QUERY,
  type SalesRepresentative,
} from '@paeffgen/react/crm-documents/data-access-graphql';
import { useAdmin } from '@paeffgen/react/crm-documents/feature-auth';
import { Skeleton } from '@paeffgen/react/crm-documents/ui';
import {
  formatHours,
  getBranchStatus,
  resolveBranch,
  type BranchConfig,
} from './branch-config';
import type { BranchEmployee, BranchEmployeesData } from './team-types';

const telHref = (s: string) => `tel:${s.replace(/[^0-9+]/g, '')}`;

// ============================================================================
// Verkäufer-Karte
// ============================================================================

function EmployeeCard({ emp, isLead }: { emp: BranchEmployee; isLead: boolean }) {
  return (
    <div
      className={`flex flex-col p-4 rounded-xl bg-white transition-shadow hover:shadow-[0_8px_22px_-12px_rgba(0,0,0,0.25)] ${
        isLead ? 'border border-primary ring-1 ring-primary' : 'border border-zinc-200'
      }`}
    >
      <div className="flex items-center gap-3">
        <Image
          src={emp.photoUrl}
          alt={emp.name}
          width={88}
          height={88}
          className="w-[88px] h-[88px] rounded-xl object-cover bg-zinc-100 shrink-0"
        />
        <div className="min-w-0">
          {isLead && (
            <div className="flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-wide text-primary mb-1">
              <Star size={13} className="fill-current shrink-0" />
              Ansprechpartner
            </div>
          )}
          <div className="text-[15px] font-bold text-secondary tracking-tight leading-tight">
            {emp.name}
          </div>
          <div className="text-xs text-text-muted mt-0.5">{emp.role}</div>
        </div>
      </div>

      {/* Kontaktzeilen */}
      <div className="flex flex-col gap-1.5 mt-2.5">
        {emp.phone && (
          <div className="flex items-center gap-2 text-xs text-text-muted min-w-0">
            <Phone size={14} className="text-zinc-400 shrink-0" />
            <a href={telHref(emp.phone)} className="tabular-nums hover:text-primary truncate">
              {emp.phone}
            </a>
          </div>
        )}
        <div className="flex items-center gap-2 text-xs text-text-muted min-w-0">
          <Mail size={14} className="text-zinc-400 shrink-0" />
          <a href={`mailto:${emp.email}`} className="hover:text-primary truncate">
            {emp.email}
          </a>
        </div>
        {emp.fax && (
          <div className="flex items-center gap-2 text-[11px] text-zinc-400 min-w-0">
            <Printer size={14} className="text-zinc-400 shrink-0" />
            <span className="tabular-nums truncate">Fax {emp.fax}</span>
          </div>
        )}
      </div>

      {/* Aktionen */}
      <div className="flex gap-2 mt-auto pt-3.5">
        {emp.phone && (
          <a
            href={telHref(emp.phone)}
            className="flex-1 inline-flex items-center justify-center gap-1.5 h-[38px] rounded-lg text-[13px] font-bold bg-primary text-white border border-primary hover:bg-primary-hover transition-colors"
          >
            <Phone size={15} />
            Anrufen
          </a>
        )}
        <a
          href={`mailto:${emp.email}`}
          className="flex-1 inline-flex items-center justify-center gap-1.5 h-[38px] rounded-lg text-[13px] font-bold bg-white text-secondary border border-zinc-300 hover:bg-zinc-50 transition-colors"
        >
          <Mail size={15} />
          E-Mail
        </a>
      </div>
    </div>
  );
}

// ============================================================================
// Team-Karte (links, Belege-Breite)
// ============================================================================

function TeamCard({
  branch,
  employees,
  leadEmail,
}: {
  branch: BranchConfig;
  employees: BranchEmployee[];
  leadEmail: string | null;
}) {
  return (
    <div className="lg:col-span-2 bg-white rounded-xl border border-zinc-200 shadow-[0_2px_4px_rgba(44,44,43,0.05)] p-6">
      <div className="flex items-baseline justify-between gap-4 mb-5">
        <p className="text-base font-bold text-secondary tracking-tight">
          Ihr Verkaufsteam in {branch.name}
        </p>
        <span className="text-xs text-zinc-400 shrink-0">
          {employees.length} Verkäufer · persönlich für Sie da
        </span>
      </div>
      {/* 2-spaltig: die Team-Karte liegt im selben 3-Spalten-Raster (col-span-2)
          wie „Letzte Belege" darüber; bei der Breite passen 2 Verkäufer-Karten. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {employees.map((emp) => (
          <EmployeeCard
            key={emp.email}
            emp={emp}
            isLead={!!leadEmail && emp.email.toLowerCase() === leadEmail}
          />
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Filial-Kachel (rechts, Anfrage-Breite)
// ============================================================================

function BranchTile({ branch }: { branch: BranchConfig }) {
  const status = getBranchStatus(branch);

  return (
    <div className="bg-white rounded-xl border border-zinc-200 shadow-[0_2px_4px_rgba(44,44,43,0.05)] p-6 flex flex-col">
      <span className="w-11 h-11 rounded-xl bg-orange-50 text-primary flex items-center justify-center">
        <MapPin size={22} />
      </span>
      <h3 className="text-lg font-bold text-secondary tracking-tight mt-3.5">
        Filiale {branch.name}
      </h3>

      {status.isOpen ? (
        <span className="self-start mt-2.5 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-status-delivery bg-status-delivery-soft px-2.5 py-1 rounded-full">
          <span className="w-1.5 h-1.5 rounded-full bg-status-delivery shadow-[0_0_0_3px_rgba(84,143,37,0.2)]" />
          Geöffnet{status.closingTime ? ` · bis ${status.closingTime}` : ''}
        </span>
      ) : (
        <span className="self-start mt-2.5 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-status-credit bg-status-credit-soft px-2.5 py-1 rounded-full">
          {status.label}
          {status.openingTime ? ` · öffnet ${status.openingTime}` : ''}
        </span>
      )}

      <div className="mt-4 mb-4 flex flex-col">
        <div className="flex flex-col gap-1 py-3">
          <span className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-zinc-400">
            <Clock size={14} className="shrink-0" />
            Öffnungszeiten
          </span>
          <b className="text-sm font-semibold text-secondary tabular-nums pl-[22px]">
            {formatHours(branch)}
          </b>
        </div>
        <div className="flex flex-col gap-1 py-3 border-t border-zinc-100">
          <span className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-zinc-400">
            <Phone size={14} className="shrink-0" />
            Telefon
          </span>
          <b className="text-sm font-semibold text-secondary tabular-nums pl-[22px]">
            <a href={telHref(branch.phone)} className="text-inherit no-underline hover:text-primary">
              {branch.phone}
            </a>
          </b>
        </div>
      </div>

      {/* QR öffnet das Kundenportal auf dem Smartphone (generischer /qrcode.svg,
          NICHT filialspezifisch — daher keine „Filiale"-Formulierung). */}
      <div className="mt-auto flex items-center gap-3 p-2.5 px-3.5 border border-zinc-200 rounded-xl">
        <Image
          src="/qrcode.svg"
          alt="QR-Code: Kundenportal mobil öffnen"
          width={44}
          height={44}
          unoptimized
          className="w-11 h-11 rounded shrink-0"
        />
        <div>
          <b className="text-sm font-bold text-secondary block">Kundenportal mobil öffnen</b>
          <span className="text-[11px] text-text-muted">QR-Code mit dem Handy scannen</span>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MOBILE (< md) — kompakte Variante (Mobile Verkaufsteam.html)
// ============================================================================

/** Kompakte Verkäufer-Karte: kleineres Foto, nur eine „Durchwahl"-Zeile, Mail via Button. */
function MobileEmployeeCard({ emp, isLead }: { emp: BranchEmployee; isLead: boolean }) {
  return (
    <div
      className={`flex flex-col rounded-[14px] p-3.5 bg-white ${
        isLead ? 'border border-primary ring-1 ring-primary' : 'border border-zinc-200'
      }`}
    >
      <div className="flex items-center gap-3">
        <Image
          src={emp.photoUrl}
          alt={emp.name}
          width={88}
          height={88}
          className="w-[60px] h-[60px] rounded-[13px] object-cover bg-zinc-100 border border-zinc-200 shrink-0"
        />
        <div className="min-w-0 flex-1">
          {isLead && (
            <div className="flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-wide text-primary mb-0.5">
              <Star size={12} className="fill-current shrink-0" />
              Ihr Ansprechpartner
            </div>
          )}
          <div className="text-[15px] font-bold text-secondary tracking-tight leading-tight">
            {emp.name}
          </div>
          <div className="text-xs text-text-muted mt-0.5">{emp.role}</div>
          {emp.phone && (
            <div className="flex items-center gap-1.5 text-[11px] text-zinc-400 mt-1 tabular-nums">
              <Phone size={12} className="shrink-0" />
              Durchwahl {emp.phone}
            </div>
          )}
        </div>
      </div>
      <div className="flex gap-2 mt-3">
        {emp.phone && (
          <a
            href={telHref(emp.phone)}
            className="flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-xl text-sm font-bold bg-primary text-white"
          >
            <Phone size={16} />
            Anrufen
          </a>
        )}
        <a
          href={`mailto:${emp.email}`}
          className="flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-xl text-sm font-bold bg-zinc-50 text-secondary border border-zinc-200"
        >
          <Mail size={16} />
          E-Mail
        </a>
      </div>
    </div>
  );
}

/** Mobile Team-Liste: schlichter Header „Ihr Verkaufsteam" + gestapelte Karten (ohne Box). */
function MobileTeam({
  branch,
  employees,
  leadEmail,
}: {
  branch: BranchConfig;
  employees: BranchEmployee[];
  leadEmail: string | null;
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3 mb-3 px-0.5">
        <span className="text-[17px] font-bold text-secondary tracking-tight">
          Ihr Verkaufsteam
        </span>
        <span className="text-xs font-semibold text-zinc-400 whitespace-nowrap">
          {employees.length} Verkäufer · {branch.name}
        </span>
      </div>
      <div className="flex flex-col gap-2.5">
        {employees.map((emp) => (
          <MobileEmployeeCard
            key={emp.email}
            emp={emp}
            isLead={!!leadEmail && emp.email.toLowerCase() === leadEmail}
          />
        ))}
      </div>
    </div>
  );
}

/** Mobile Filial-Info: Zeilen-Layout (Status / Schließt um / Öffnungszeiten / Telefon), ohne QR. */
function MobileBranchTile({ branch }: { branch: BranchConfig }) {
  const status = getBranchStatus(branch);
  return (
    <div>
      <div className="flex items-center gap-2 mb-3 px-0.5">
        <MapPin size={16} className="text-primary shrink-0" />
        <h3 className="text-[17px] font-bold text-secondary tracking-tight">
          Filiale {branch.name}
        </h3>
      </div>
      <div className="bg-white rounded-xl border border-zinc-200 shadow-[0_2px_4px_rgba(44,44,43,0.05)] px-4 text-sm">
        <div className="flex items-center justify-between py-3">
          <span className="text-text-muted">Status</span>
          {status.isOpen ? (
            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-status-delivery bg-status-delivery-soft px-2.5 py-0.5 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-status-delivery" />
              Geöffnet
            </span>
          ) : (
            <span className="inline-flex items-center text-[11px] font-bold uppercase tracking-wide text-status-credit bg-status-credit-soft px-2.5 py-0.5 rounded-full">
              {status.label}
            </span>
          )}
        </div>
        {status.isOpen ? (
          <div className="flex items-center justify-between py-3 border-t border-zinc-100">
            <span className="text-text-muted">Schließt um</span>
            <b className="font-semibold text-secondary tabular-nums">{status.closingTime}</b>
          </div>
        ) : status.openingTime ? (
          <div className="flex items-center justify-between py-3 border-t border-zinc-100">
            <span className="text-text-muted">Öffnet wieder</span>
            <b className="font-semibold text-secondary tabular-nums">{status.openingTime}</b>
          </div>
        ) : null}
        <div className="flex items-center justify-between py-3 border-t border-zinc-100">
          <span className="text-text-muted">Öffnungszeiten</span>
          <b className="font-semibold text-secondary tabular-nums">{formatHours(branch)}</b>
        </div>
        <div className="flex items-center justify-between py-3 border-t border-zinc-100">
          <span className="text-text-muted">Telefon</span>
          <b className="font-semibold text-secondary tabular-nums">
            <a href={telHref(branch.phone)} className="text-inherit no-underline">
              {branch.phone}
            </a>
          </b>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Block je Filiale — getrennte Mobile-/Desktop-Teilbäume (per CSS umgeschaltet,
// kein JS-Media-Query → SSR-sicher). Karten-Stil teilt sich bei md (768);
// das Raster-Split (Team+Kachel nebeneinander) erst ab lg, damit es weiter
// mit „Letzte Belege"/„Anfrage" darüber fluchtet.
// ============================================================================

function BranchBlock({
  branch,
  employees,
  leadEmail,
}: {
  branch: BranchConfig;
  employees: BranchEmployee[];
  leadEmail: string | null;
}) {
  // Ansprechpartner (falls im Team) zuerst — wie im Entwurf (erste Karte = Lead).
  const ordered = [...employees].sort((a, b) => {
    const aLead = leadEmail && a.email.toLowerCase() === leadEmail ? 0 : 1;
    const bLead = leadEmail && b.email.toLowerCase() === leadEmail ? 0 : 1;
    return aLead - bLead;
  });

  return (
    <>
      {/* Mobile (< md) */}
      <div className="md:hidden space-y-6">
        <MobileTeam branch={branch} employees={ordered} leadEmail={leadEmail} />
        <MobileBranchTile branch={branch} />
      </div>

      {/* Desktop (>= md): gleiches Raster wie das Dashboard-Hauptgrid,
          Split erst ab lg (Team-Karte col-span-2, Filial-Kachel 1 Spalte). */}
      <div className="hidden md:grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
        {ordered.length > 0 && (
          <TeamCard branch={branch} employees={ordered} leadEmail={leadEmail} />
        )}
        <BranchTile branch={branch} />
      </div>
    </>
  );
}

// ============================================================================
// Sektion
// ============================================================================

function SectionSkeleton() {
  return (
    <section className="space-y-4">
      <Skeleton className="h-6 w-64" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 bg-white rounded-xl border border-zinc-200 shadow-[0_2px_4px_rgba(44,44,43,0.05)] p-6">
          <Skeleton className="h-5 w-48 mb-5" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-52 w-full rounded-xl" />
            ))}
          </div>
        </div>
        <Skeleton className="h-72 w-full rounded-xl" />
      </div>
    </section>
  );
}

/**
 * Dashboard-Sektion „Ihr Standort & Ansprechpartner" (responsive: Desktop Variante A,
 * Mobile kompakt). Die angezeigte Filiale wird aus der NAV-Heimatfiliale des
 * Ansprechpartners (`salesRepresentative.locationCode`) abgeleitet — der Rep ist der
 * Verkäufer auf dem jüngsten Beleg des Kunden und damit der beste Proxy für „wo kauft
 * der Kunde ein". Lässt sich kein physischer Standort auflösen (kein Rep / ORDERSHOP /
 * unbekannter Code), wird die Sektion ausgeblendet. Der hervorgehobene Ansprechpartner
 * wird per E-Mail-Abgleich gegen das Filial-Team bestimmt; passt keiner, kein Badge.
 *
 * @param branchEmployees Statische, von der App validierte Team-Daten (branch-employees.json).
 */
export function StandortAnsprechpartner({
  branchEmployees,
}: {
  branchEmployees: BranchEmployeesData;
}) {
  const { adminQueryVars } = useAdmin();

  // cache-first → teilt sich den Cache mit der Kontakt-Karte im Dashboard-Header.
  const { data: repData, loading: repLoading } = useQuery<{
    salesRepresentative: SalesRepresentative | null;
  }>(SALES_REPRESENTATIVE_QUERY, {
    variables: { ...adminQueryVars },
    fetchPolicy: 'cache-first',
  });

  const rep = repData?.salesRepresentative ?? null;
  const leadEmail = rep?.email?.toLowerCase() ?? null;

  if (repLoading) return <SectionSkeleton />;

  // Filiale aus der NAV-Heimatfiliale des Ansprechpartners. Kein auflösbarer
  // Standort → nichts anzeigen (statt früher „beide Filialen").
  const branch = resolveBranch(rep?.locationCode);
  if (!branch) return null;

  return (
    <section className="space-y-4">
      {/* Auf Mobile zeigt der Entwurf keinen „Ihr Standort"-Titel — nur „Ihr Verkaufsteam". */}
      <h2 className="hidden md:block text-xl font-bold text-secondary tracking-tight">
        Ihr Standort &amp; Ansprechpartner
      </h2>
      <div className="space-y-10 md:space-y-6">
        <BranchBlock
          branch={branch}
          employees={branchEmployees.branches[branch.code]?.employees ?? []}
          leadEmail={leadEmail}
        />
      </div>
    </section>
  );
}
